import { Controller, useForm } from 'react-hook-form';
import { Trans } from '@lingui/react';
import { Sheet } from '@startuptools/ui/joy/Sheet';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Button } from '@startuptools/ui/joy/Button';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Alert } from '@startuptools/ui/joy/Alert';
import { OnReject } from './types';
import { Radio } from '@startuptools/ui/joy/Radio';
import { RadioGroup } from '@startuptools/ui/joy/RadioGroup';
import { isNil } from 'lodash-es';
import { useState } from 'react';
import { FormControl } from '@startuptools/ui/joy/FormControl';

interface FormData {
  scopeOfPractice: boolean;
}

export const Practice = ({ onReject }: { onReject: OnReject }) => {
  const { stepNext } = useStepper();
  const [showMore, setShowMore] = useState(false);

  const { handleSubmit, control, watch } = useForm<FormData>();

  const onFormSubmit = handleSubmit(async ({ scopeOfPractice }) => {
    if (scopeOfPractice) {
      onReject({
        title: <Trans id="The company operates in a restricted business area" />,
        description: (
          <Trans id="the company operates in a business area that is not eligible for qualified employee stock options" />
        ),
      });
    } else {
      await stepNext();
    }
  });

  const formValues = watch();

  return (
    <form onSubmit={onFormSubmit}>
      <Sheet
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
        }}
      >
        <Typography level="h4">
          <Trans id="Scope of Business" />
        </Typography>

        <Alert
          variant="outlined"
          color="primary"
          endDecorator={
            <Button variant="plain" onClick={() => setShowMore(!showMore)}>
              <Trans id="More info" />
            </Button>
          }
        >
          <Stack spacing={2}>
            <Typography level="body-sm">
              <Trans id="Favorable employee stock options do not apply to companies in certain specific areas." />
            </Typography>
            {showMore && (
              <Typography level="body-sm">
                <Trans id="For example, legislators wanted to prevent law firms and accounting firms from using this method to reward staff. The coal and steel prohibition is an EU requirement." />
              </Typography>
            )}
          </Stack>
        </Alert>

        <Typography>
          <Trans id="Does the company operate in any of the following areas?" />
        </Typography>

        <ul>
          <li>
            <Typography level="body-sm">
              <Trans id="Legal, tax and accounting advice or auditing" />
            </Typography>
          </li>
          <li>
            <Typography level="body-sm">
              <Trans id="Banking or financing operations" />
            </Typography>
          </li>
          <li>
            <Typography level="body-sm">
              <Trans id="Insurance operations" />
            </Typography>
          </li>
          <li>
            <Typography level="body-sm">
              <Trans id="Production of coal and steel" />
            </Typography>
          </li>
          <li>
            <Typography level="body-sm">
              <Trans id="Trading in land, real estate, commodities or financial instruments" />
            </Typography>
          </li>
          <li>
            <Typography level="body-sm">
              <Trans id="Long-term rental of premises or housing" />
            </Typography>
          </li>
        </ul>

        <Sheet>
          <Controller
            control={control}
            name="scopeOfPractice"
            render={({ field: { value, onChange, ...field } }) => (
              <FormControl>
                <RadioGroup {...field} value={value} onChange={e => onChange(e.target.value === 'true')}>
                  <Radio value="false" label={<Trans id="No" />} />
                  <Radio value="true" label={<Trans id="Yes" />} />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Sheet>

        <Sheet sx={{ pt: 2 }}>
          <Button type="submit" disabled={isNil(formValues.scopeOfPractice)}>
            <Trans id="Continue" />
          </Button>
        </Sheet>
      </Sheet>
    </form>
  );
};
